import * as React from "react";
import { useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import { CLO_CHANNELS, cloError, cloPatch, cloPost, ENDPOINTS, MATCH_TYPES } from "./api";

interface Props {
  open: boolean;
  cloMerchantId: number;
  pattern: any | null;
  initialText?: string;
  onClose: () => void;
  onSaved: () => void;
}

const PatternDialog: React.FC<Props> = ({ open, cloMerchantId, pattern, initialText, onClose, onSaved }) => {
  const notify = useNotify();
  const [form, setForm] = useState({ pattern: "", matchType: "starts_with", channel: "" });
  const [preview, setPreview] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      pattern: pattern?.pattern ?? initialText ?? "",
      matchType: pattern?.match_type ?? "starts_with",
      channel: pattern?.channel ?? "",
    });
    setPreview(null);
  }, [open, pattern, initialText]);

  const body = (dryRun: boolean) => ({
    pattern: form.pattern,
    matchType: form.matchType,
    channel: form.channel || null,
    dryRun,
  });

  const send = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const res = pattern
        ? await cloPatch(`${ENDPOINTS.CLO_MERCHANT_PATTERNS}/${pattern.id}`, body(dryRun))
        : await cloPost(ENDPOINTS.CLO_MERCHANT_PATTERNS, { cloMerchantId, ...body(dryRun) });
      if (dryRun) {
        setPreview(res.data);
      } else {
        notify(pattern ? "Pattern updated as a new version" : "Pattern added", "info");
        onSaved();
      }
    } catch (e) {
      notify(cloError(e), "warning");
    } finally {
      setBusy(false);
    }
  };

  const change = (key: string, value: string) => {
    setForm({ ...form, [key]: value });
    setPreview(null);
  };

  return (
    <Dialog disableBackdropClick open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{pattern ? `Edit pattern (version ${pattern.version})` : "Add bank text pattern"}</DialogTitle>
      <DialogContent>
        <TextField fullWidth margin="dense" label="Pattern" value={form.pattern} onChange={(e) => change("pattern", e.target.value)} />
        <TextField select fullWidth margin="dense" label="Match type" value={form.matchType} onChange={(e) => change("matchType", e.target.value)}>
          {Object.entries(MATCH_TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField select fullWidth margin="dense" label="Channel" value={form.channel} onChange={(e) => change("channel", e.target.value)}>
          <MenuItem value="">Not known from the text</MenuItem>
          {CLO_CHANNELS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        {preview && (
          <Box mt={2}>
            <Typography variant="body2">
              Matches <b>{preview.matched}</b> of the latest {preview.sampledRows.toLocaleString("en-GB")} bank transactions
              {preview.matchedByOtherMerchants > 0 && <> · <b>{preview.matchedByOtherMerchants}</b> already match another merchant</>}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Bank text</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.examples.map((e: any) => (
                  <TableRow key={e.description}>
                    <TableCell>{e.description}</TableCell>
                    <TableCell align="right">{e.total}</TableCell>
                  </TableRow>
                ))}
                {preview.examples.length === 0 && (
                  <TableRow><TableCell colSpan={2} align="center">No matches in the sample</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={busy || !form.pattern.trim()} onClick={() => send(true)}>Preview</Button>
        <Button color="primary" variant="contained" disabled={busy || !preview} onClick={() => send(false)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatternDialog;
